import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCodeStyling from "qr-code-styling";
import Button from "components/shared/Button";
import { Input } from "components/ui/input";
import Dialog from "components/shared/Dialog";
import { useProfile } from "../../../../hooks/useProfile";
import { useChangeUserInformationMutation } from "../../../../api/useChangeUserInformationMutation";

const qrCode = new QRCodeStyling({
    width: 300,
    height: 300,
    data: "",
    image: "/assets/images/logo_min.png",
    dotsOptions: { color: "#000", type: "rounded" },
    imageOptions: { crossOrigin: "anonymous", margin: 20 },
});

function QrCode({ isOpen, close }: { isOpen: boolean; close: () => void }) {
    const { t } = useTranslation();
    const [token, setToken] = useState("");
    const { user } = useProfile();
    const { authVerify } = useChangeUserInformationMutation();

    const submit = useCallback(() => {
        if (!token) return;
        authVerify.mutate(
            { token },
            {
                onSuccess: () => {
                    localStorage.setItem("OTP_VALIDATE", "true");
                    close();
                },
            },
        );
    }, [authVerify, token, close]);

    useEffect(() => {
        qrCode.update({
            data: `${user?.twoFA?.otp_auth_url}?secret=${user?.twoFA?.otp_base32}`,
        });
    }, [user?.twoFA]);

    const qrRef = useCallback((node: HTMLDivElement | null) => {
        if (!node) return;
        node.innerHTML = "";
        qrCode.append(node);
    }, []);

    return (
        <Dialog open={isOpen} close={close} title={`${t("two-factor-auth")} (2FA)`} size="lg">
            <div className="tw-space-y-5 tw-mb-4">
                <div>
                    <h4 className="tw-text-primary tw-border-b tw-pb-1 tw-mb-2 tw-font-semibold tw-text-sm">
                        {t("config-google-auth")}
                    </h4>
                    <ol className="tw-list-decimal tw-pl-5 tw-space-y-1 tw-text-sm tw-text-muted-foreground">
                        <li>{t("install-google-authenticator")}</li>
                        <li>{t("select-plus-icon")}</li>
                        <li>{t("select-scan-a-barcode")}</li>
                    </ol>
                </div>

                <div>
                    <h4 className="tw-text-primary tw-border-b tw-pb-1 tw-mb-2 tw-font-semibold tw-text-sm">
                        {t("scan-qr-code-to-generate-verify-code")}
                    </h4>
                    <div ref={qrRef} className="tw-flex tw-justify-center" />
                </div>

                <div>
                    <h4 className="tw-text-primary tw-border-b tw-pb-1 tw-mb-2 tw-font-semibold tw-text-sm">
                        {t("enter-code-into-your-app")}
                    </h4>
                    <div className="tw-flex tw-gap-2 tw-text-sm">
                        <span className="tw-font-semibold">{t("secret-key")}:</span>
                        <span className="tw-text-muted-foreground">{user?.twoFA?.otp_base32}</span>
                    </div>
                </div>

                <div>
                    <h4 className="tw-text-primary tw-border-b tw-pb-1 tw-mb-2 tw-font-semibold tw-text-sm">
                        {t("verify-code")}
                    </h4>
                    <p className="tw-text-xs tw-text-muted-foreground tw-mb-2">{t("verify-auth-code")}</p>
                    <Input
                        placeholder={t("auth-code")}
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && submit()}
                    />
                </div>
            </div>

            <div className="tw-flex tw-justify-end tw-gap-2">
                <Button variant="outline" onClick={close} disabled={authVerify.isPending}>
                    {t("close")}
                </Button>
                <Button onClick={submit} disabled={!token} loading={authVerify.isPending}>
                    {t("verify-and-activate")}
                </Button>
            </div>
        </Dialog>
    );
}

export default QrCode;
